import React, { useEffect, useState } from 'react';
import { ShoppingBag, ArrowRight, Zap, Truck } from 'lucide-react';
import { getPlayerProfile, getUsernameFromProfileId } from '../../../../services/firestoreService';
import { db } from '../../../../config/firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import useBlockStyles from '../common/useBlockStyles';
import { trackEvent } from '../../../../utils/analytics';

const STYLE_OPTIONS = {
  defaultBackground: 'section',
  defaultHeadingSize: 'medium',
  defaultAlignment: 'center',
  defaultPadding: 'normal',
};

/**
 * ShopRenderer - Renders the shop products block
 * Fetches active products and displays them with theme styling
 */
const ShopRenderer = ({ block, profileId, theme = {} }) => {
  const { content } = block;
  const {
    heading = 'Shop',
    showPrices = true,
    maxProducts = 6,
  } = content;

  const [products, setProducts] = useState([]);
  const [username, setUsername] = useState(null);
  const [loading, setLoading] = useState(true);

  const styles = useBlockStyles(content, theme, STYLE_OPTIONS);

  useEffect(() => {
    const loadProducts = async () => {
      if (!profileId) {
        setLoading(false);
        return;
      }

      try {
        const [profile, fetchedUsername] = await Promise.all([
          getPlayerProfile(profileId),
          getUsernameFromProfileId(profileId),
        ]);

        setUsername(fetchedUsername);

        // vendorId is the user's Firebase Auth userId
        const vendorId = profile?.userId || profileId;

        // Get active products
        const q = query(
          collection(db, 'products'),
          where('vendorId', '==', vendorId),
          where('isActive', '==', true)
        );
        const snapshot = await getDocs(q);
        const productsData = snapshot.docs
          .map((doc) => ({ id: doc.id, ...doc.data() }))
          .slice(0, maxProducts);

        setProducts(productsData);
      } catch (error) {
        console.error('Error loading products:', error);
      } finally {
        setLoading(false);
      }
    };

    loadProducts();
  }, [profileId, maxProducts]);

  // Helper to find best product image
  const getProductImage = (product) => {
    // For POD products, try to find Black variant mockup
    if (product.product_type === 'pod' && product.variants?.length > 0) {
      const firstVariant = product.variants[0];
      if (firstVariant.mockup_images && firstVariant.variant_map) {
        const blackVariantKey = Object.keys(firstVariant.variant_map).find((key) =>
          key.startsWith('black_')
        );
        if (blackVariantKey) {
          const blackPrintifyVariantId = firstVariant.variant_map[blackVariantKey];
          const blackMockup = firstVariant.mockup_images.find(
            (img) => img.variant_ids && img.variant_ids.includes(blackPrintifyVariantId)
          );
          if (blackMockup?.src) return blackMockup.src;
        }
      }
      return product.preview_image || firstVariant?.mockup_images_urls?.[0] || null;
    }
    return product.preview_image || product.imageUrl || null;
  };

  // Placeholder when no products
  if (!loading && products.length === 0) {
    return (
      <div
        className={`w-full ${styles.sectionPaddingClass}`}
        style={{ backgroundColor: styles.sectionBackground }}
      >
        <div className="max-w-4xl mx-auto text-center">
          <div
            className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
            style={{ backgroundColor: `${styles.accentColor}20` }}
          >
            <ShoppingBag size={32} style={{ color: styles.accentColor }} />
          </div>
          <h3
            className="text-xl font-semibold mb-2"
            style={{ color: styles.headingColor, fontFamily: styles.headingFontStyle }}
          >
            No Products Yet
          </h3>
          <p style={{ color: styles.mutedColor }}>Products will appear here once added to the shop.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        className={`w-full ${styles.sectionPaddingClass}`}
        style={{ backgroundColor: styles.sectionBackground }}
      >
        <div className="max-w-4xl mx-auto flex items-center justify-center">
          <div
            className="animate-spin w-8 h-8 border-3 border-gray-600 rounded-full"
            style={{ borderTopColor: styles.accentColor }}
          ></div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`w-full ${styles.sectionPaddingClass}`}
      style={{ backgroundColor: styles.sectionBackground }}
    >
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        {heading && (
          <div className="flex items-center justify-between mb-8">
            <h2
              className={`${styles.headingSizeClass} font-bold`}
              style={{ color: styles.headingColor, fontFamily: styles.headingFontStyle }}
            >
              {heading}
            </h2>
            {username && products.length >= maxProducts && (
              <a
                href={`/${username}/shop`}
                className="flex items-center gap-2 text-sm font-medium transition-opacity hover:opacity-80"
                style={{ color: styles.accentColor }}
              >
                View All
                <ArrowRight size={16} />
              </a>
            )}
          </div>
        )}

        {/* Products Grid - Compact cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
          {products.map((product) => {
            const image = getProductImage(product);
            const isPOD = product.product_type === 'pod';

            return (
              <a
                key={product.id}
                href={`/product/${username || profileId}/${product.id}`}
                onClick={() => trackEvent('Product Click', { coach: username, productId: product.id })}
                className="group block rounded-lg overflow-hidden transition-all hover:translate-y-[-2px]"
                style={{
                  backgroundColor: styles.cardBackground,
                  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
                }}
              >
                {/* Product Image */}
                <div className="aspect-square overflow-hidden relative">
                  {image ? (
                    <img
                      src={image}
                      alt={product.title}
                      className="w-full h-full object-cover transition-transform group-hover:scale-105"
                    />
                  ) : (
                    <div
                      className="w-full h-full flex items-center justify-center"
                      style={{ backgroundColor: 'rgba(0,0,0,0.3)' }}
                    >
                      <ShoppingBag size={32} style={{ color: styles.mutedColor }} />
                    </div>
                  )}

                  {/* Product Type Badge - smaller */}
                  <div className="absolute top-2 right-2">
                    {isPOD ? (
                      <span
                        className="px-1.5 py-0.5 rounded-full text-[10px] font-medium flex items-center gap-0.5"
                        style={{ backgroundColor: '#3b82f6', color: '#ffffff' }}
                      >
                        <Truck size={10} />
                        Ships
                      </span>
                    ) : (
                      <span
                        className="px-1.5 py-0.5 rounded-full text-[10px] font-medium flex items-center gap-0.5"
                        style={{ backgroundColor: '#10b981', color: '#ffffff' }}
                      >
                        <Zap size={10} />
                        Instant
                      </span>
                    )}
                  </div>
                </div>

                {/* Content - Compact */}
                <div className="p-2 sm:p-3">
                  {/* Title */}
                  <h3
                    className="text-sm font-semibold mb-1 line-clamp-1 group-hover:underline"
                    style={{ color: styles.headingColor, fontFamily: styles.headingFontStyle }}
                  >
                    {product.title}
                  </h3>

                  {/* Price */}
                  {showPrices && (
                    <span className="text-sm font-bold" style={{ color: styles.accentColor }}>
                      {product.price_min
                        ? `$${(product.price_min / 100).toFixed(2)}+`
                        : `$${(product.price / 100).toFixed(2)}`}
                    </span>
                  )}
                </div>
              </a>
            );
          })}
        </div>

        {/* View All Button (mobile) */}
        {username && products.length >= maxProducts && (
          <div className="mt-8 text-center lg:hidden">
            <a
              href={`/${username}/shop`}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-opacity hover:opacity-90"
              style={{ backgroundColor: styles.accentColor, color: '#ffffff' }}
            >
              View All Products
              <ArrowRight size={16} />
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShopRenderer;
